import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Clock, Download, FileText, History, Wallet } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/auth-provider';
import { fetchPaginated } from '@/lib/supabase-utils';
import { calculateSessionHours } from '@/lib/time-utils';
import { getQatarDayRange } from '@/utils/timezone';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { downloadSpreadsheet } from '@/lib/spreadsheet-browser';

interface MyPayrollRow {
  payroll_row_id: string;
  payroll_upload_id: string;
  staff_id: string;
  employee_name: string | null;
  department: string | null;
  position: string | null;
  payroll_month: string;
  basic_salary: number;
  hourly_rate: number;
  tracked_hours: number;
  overtime_hours: number;
  deduction_hours: number;
  deduction_amount: number;
  net_salary: number;
  payroll_status: 'approved';
  approved_at: string | null;
}

interface TimesheetRow {
  id: string;
  start_time: string;
  end_time: string | null;
  deducted_seconds?: number | null;
  is_idle?: boolean | null;
}

const round2 = (value: number) => Math.round(value * 100) / 100;
const money = (value: number) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hours = (value: number) => Number(value || 0).toFixed(2);

function monthBounds(month: string) {
  const firstDay = `${month}-01`;
  const [year, monthNumber] = month.split('-').map(Number);
  const lastDay = `${month}-${String(new Date(Date.UTC(year, monthNumber, 0)).getUTCDate()).padStart(2, '0')}`;
  return { start: getQatarDayRange(firstDay).start, end: getQatarDayRange(lastDay).end };
}

async function exportRows(fileName: string, sheetName: string, rows: Record<string, unknown>[]) {
  await downloadSpreadsheet(fileName, sheetName, rows);
}

export default function MyPayrollPage() {
  const { userDetails } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [payrollRows, setPayrollRows] = useState<MyPayrollRow[]>([]);
  const [timesheet, setTimesheet] = useState<TimesheetRow[]>([]);
  const [loading, setLoading] = useState(true);

  const selectedPayroll = useMemo(
    () => payrollRows.find((row) => format(new Date(row.payroll_month), 'yyyy-MM') === selectedMonth) || null,
    [payrollRows, selectedMonth]
  );

  const timesheetSummary = useMemo(() => {
    const trackedHours = round2(timesheet.reduce((sum, log) => sum + (log.end_time ? calculateSessionHours(log.start_time, log.end_time) : 0), 0));
    const deductedHours = round2(timesheet.reduce((sum, log) => sum + Number(log.deducted_seconds || 0) / 3600, 0));
    return {
      sessions: timesheet.length,
      trackedHours,
      deductedHours,
      activeHours: round2(Math.max(0, trackedHours - deductedHours)),
    };
  }, [timesheet]);

  useEffect(() => {
    fetchMyPayroll();
  }, []);

  const fetchMyTimesheet = useCallback(async () => {
    if (!userDetails?.id) return;
    try {
      const { start, end } = monthBounds(selectedMonth);
      const query = supabase
        .from('time_logs')
        .select('id,start_time,end_time,deducted_seconds,is_idle')
        .eq('user_id', userDetails.id)
        .gte('start_time', start)
        .lte('start_time', end)
        .order('start_time', { ascending: false });
      const logs = await fetchPaginated<any>(query);
      setTimesheet(logs || []);
    } catch (error) {
      console.error('Error loading my timesheet:', error);
      toast.error('Failed to load timesheet');
    }
  }, [selectedMonth, userDetails?.id]);

  useEffect(() => {
    fetchMyTimesheet();
  }, [fetchMyTimesheet]);

  const fetchMyPayroll = async () => {
    setLoading(true);
    try {
      const { data, error } = await (supabase as any).rpc('get_my_approved_payroll');
      if (error) throw error;
      setPayrollRows((data || []) as MyPayrollRow[]);
    } catch (error) {
      console.error('Error loading my payroll:', error);
      toast.error('Failed to load payroll');
    } finally {
      setLoading(false);
    }
  };


  const downloadPayslip = async () => {
    if (!selectedPayroll) return;
    try {
      await exportRows(`payslip-${selectedPayroll.staff_id}-${selectedMonth}.xlsx`, 'Payslip', [payrollExportRow(selectedPayroll)]);
    } catch (error) {
      console.error('Failed to export payslip', error);
      toast.error('Failed to export payslip');
    }
  };

  const downloadTimesheet = async () => {
    try {
      await exportRows(`timesheet-${selectedMonth}.xlsx`, 'Timesheet', timesheet.map((log) => ({
        Date: format(new Date(log.start_time), 'dd MMM yyyy'),
        Start: format(new Date(log.start_time), 'HH:mm'),
        End: log.end_time ? format(new Date(log.end_time), 'HH:mm') : '-',
        Hours: log.end_time ? hours(calculateSessionHours(log.start_time, log.end_time)) : '0.00',
        'Deduction Hours': hours(Number(log.deducted_seconds || 0) / 3600),
        Status: log.is_idle ? 'Idle' : 'Tracked',
      })));
    } catch (error) {
      console.error('Failed to export timesheet', error);
      toast.error('Failed to export timesheet');
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Wallet className="h-8 w-8" /> My Payroll</h1>
          <p className="text-muted-foreground">View your approved payroll, monthly history, and timesheet summary.</p>
        </div>
        <div>
          <Label htmlFor="my-payroll-month">Payroll Month</Label>
          <Input id="my-payroll-month" type="month" value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)} className="w-44" />
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">Loading payroll...</CardContent></Card>
      ) : !selectedPayroll ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">Payroll for this month is not available yet.</CardContent></Card>
      ) : (
        <Card>
          <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle>{format(new Date(selectedPayroll.payroll_month), 'MMMM yyyy')} Payroll</CardTitle>
              <CardDescription>Approved payroll details for your Staff ID only.</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadPayslip}><Download className="h-4 w-4 mr-2" /> Download payslip</Button>
              <Button variant="outline" onClick={downloadTimesheet}><Download className="h-4 w-4 mr-2" /> Download timesheet</Button>
            </div>
          </CardHeader>
          <CardContent>
            <PayrollDetails row={selectedPayroll} />
          </CardContent>
        </Card>
      )}


      {selectedPayroll && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{timesheetSummary.sessions}</div><div className="text-sm text-muted-foreground">Timesheet sessions</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-blue-600">{hours(timesheetSummary.trackedHours)}</div><div className="text-sm text-muted-foreground">Tracked hours</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-orange-600">{hours(timesheetSummary.deductedHours)}</div><div className="text-sm text-muted-foreground">Deduction hours</div></CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5" /> Timesheet Summary</CardTitle><CardDescription>Your own tracked sessions for {format(new Date(`${selectedMonth}-01`), 'MMMM yyyy')}.</CardDescription></CardHeader>
            <CardContent>
              {!timesheet.length ? <div className="text-center py-8 text-muted-foreground">No timesheet records found for this month.</div> : (
                <div className="overflow-auto border rounded-lg max-h-[420px]">
                  <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead><TableHead>Hours</TableHead><TableHead>Deduction</TableHead><TableHead>Status</TableHead></TableRow></TableHeader><TableBody>
                    {timesheet.map((log) => <TableRow key={log.id}><TableCell>{format(new Date(log.start_time), 'dd MMM yyyy')}</TableCell><TableCell>{format(new Date(log.start_time), 'HH:mm')}</TableCell><TableCell>{log.end_time ? format(new Date(log.end_time), 'HH:mm') : '-'}</TableCell><TableCell>{log.end_time ? hours(calculateSessionHours(log.start_time, log.end_time)) : '0.00'}</TableCell><TableCell>{hours(Number(log.deducted_seconds || 0) / 3600)}</TableCell><TableCell>{log.is_idle ? 'Idle' : 'Tracked'}</TableCell></TableRow>)}
                  </TableBody></Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Monthly Payroll History</CardTitle><CardDescription>Approved payroll months available for your Staff ID.</CardDescription></CardHeader>
        <CardContent>
          {!payrollRows.length ? <div className="text-center py-8 text-muted-foreground">No approved payroll history available yet.</div> : (
            <div className="overflow-auto border rounded-lg">
              <Table><TableHeader><TableRow><TableHead>Month</TableHead><TableHead>Net Salary</TableHead><TableHead>Tracked Hours</TableHead><TableHead>Status</TableHead><TableHead>Approved</TableHead></TableRow></TableHeader><TableBody>
                {payrollRows.map((row) => <TableRow key={row.payroll_row_id} className="cursor-pointer" onClick={() => setSelectedMonth(format(new Date(row.payroll_month), 'yyyy-MM'))}><TableCell>{format(new Date(row.payroll_month), 'MMMM yyyy')}</TableCell><TableCell>{money(row.net_salary)}</TableCell><TableCell>{hours(row.tracked_hours)}</TableCell><TableCell><Badge>{row.payroll_status}</Badge></TableCell><TableCell>{row.approved_at ? format(new Date(row.approved_at), 'dd MMM yyyy') : '-'}</TableCell></TableRow>)}
              </TableBody></Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PayrollDetails({ row }: { row: MyPayrollRow }) {
  const details = payrollExportRow(row);
  return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{Object.entries(details).map(([label, value]) => <div key={label} className="rounded-lg border p-4"><div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div><div className="mt-1 font-semibold">{String(value)}</div></div>)}</div>;
}

function payrollExportRow(row: MyPayrollRow) {
  return {
    'Staff ID': row.staff_id,
    'Employee Name': row.employee_name || '-',
    Department: row.department || '-',
    Position: row.position || '-',
    'Payroll Month': format(new Date(row.payroll_month), 'MMMM yyyy'),
    'Basic Salary': money(row.basic_salary),
    'Hourly Rate': money(row.hourly_rate),
    'Tracked Hours': hours(row.tracked_hours),
    'Overtime Hours': hours(row.overtime_hours),
    'Deduction Hours': hours(row.deduction_hours),
    'Deduction Amount': money(row.deduction_amount),
    'Net Salary': money(row.net_salary),
    'Payroll Status': row.payroll_status,
  };
}
